/**
 * The build menu: a card for every blueprint (game/build/blueprints.ts), with its picture, what it
 * does and what it costs. Tapping one closes the menu and starts placing it where I stand. The
 * planet stays free beside the sheet.
 */
import type { JSX } from 'react';

import { BLUEPRINTS, blueprintId, blueprintStatus } from '@game/build/blueprints';
import { startPlacing } from '@game/build/placement';
import { spendableOf } from '@game/planet/pockets';
import { closePanel } from '@game/view/panels';
import { t } from '@i18n/index';
import { CardStrip } from '@ui/components/CardStrip';
import { CostChips } from '@ui/components/CostChips';
import { HexPicture } from '@ui/components/HexPicture';
import { Sheet } from '@ui/components/Sheet';
import { useGame, useGameState, useLocale } from '@ui/hooks';

import { blueprintArt, blueprintName, blueprintWhat } from './blueprintText';

import './build.css';

export function BuildMenu(): JSX.Element | null {
  useLocale();
  const game = useGame();
  const me = useGameState((s) => s.me);
  useGameState((s) => s.planetVersion);
  const view = game.store.get().view;
  if (!me || !view) return null;
  const pockets = spendableOf(view, me.id);
  return (
    <Sheet
      id="build"
      title={t('build.title')}
      onClose={() => closePanel(game)}
      className="build"
      free
    >
      <CardStrip label={t('build.title')}>
        {BLUEPRINTS.map((blueprint) => {
          const status = blueprintStatus(view, me.id, blueprint);
          const name = blueprintName(blueprint);
          const free = Object.keys(status.cost).length === 0;
          return (
            <li key={blueprintId(blueprint)}>
              <button
                type="button"
                className="bcard"
                data-blueprint={blueprintId(blueprint)}
                aria-label={t('build.pick-aria', { name: name.toLowerCase() })}
                onClick={() => startPlacing(game, blueprint)}
              >
                <HexPicture look={blueprintArt(blueprint)} />
                <span className="about">
                  <b>{name}</b>
                  <small>{blueprintWhat(blueprint)}</small>
                  {free ? (
                    <em className="free">{t('build.free')}</em>
                  ) : (
                    <CostChips cost={status.cost} have={pockets} />
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </CardStrip>
    </Sheet>
  );
}
